import { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { Portal, usePortalNestProvider } from '@elfsight/widget-modal';
import { Loader } from '@elfsight/widget-ui';
import { useSettings } from '@wu/widget-core';
import { classnamePrefix } from '../../../constants';
import { DataWithCache } from '../../api';
import { hasMixedSources, processSource } from '../../helpers';
import { sourceContext } from '../../SourceContext';
import { Error, errorContext } from '../common';
import { currentVideoContext } from '../currentVideoContext';
import { Popup } from '../popup';
import { VideoCard } from '../videos/video';
import { GridLayout } from './Grid';
import { Playlist } from './playlistLayout';
import { SliderLayout } from './Slider';

// TODO: Убрать userId и оставить только auth

export const Layout = ({ mobile, widgetWidth }) => {
  const { layout, userId, auth, sort: settingsSort, textColor } = useSettings();

  const nest = usePortalNestProvider();

  const { videosSource } = useContext(sourceContext);
  const [sourceChanged, setSourceChanged] = useState(false);
  const [{ source, sort }, setSource] = useState(
    processSource({ source: videosSource, sort: settingsSort })
  );
  const { errorText, setErrorText } = useContext(errorContext);

  const { currentVideo, setCurrentVideo, setPlaying, setAutoplay, setPercent } =
    useContext(currentVideoContext);
  const LayoutArray = {
    playlist: Playlist,
    grid: GridLayout,
    slider: SliderLayout
  }[layout];

  useLayoutEffect(() => {
    setSourceChanged(false);
    if (hasMixedSources(videosSource)) {
      setErrorText(
        'Mixing sources of different types is currently not supported!'
      );
    } else {
      setErrorText(null);
      const processedSource = processSource({
        source: videosSource,
        sort: settingsSort
      });
      setSource(processedSource);
    }
  }, [videosSource, settingsSort, setErrorText]);

  useEffect(() => {
    if (errorText && typeof errorText === 'object' && errorText.code) {
      if (errorText.code === 429) {
        setErrorText(429);
      } else if (errorText.code === 400) {
        setErrorText(400);
      }
    }
  }, [errorText, setErrorText]);

  useLayoutEffect(() => {
    if (!userId && !auth?.identity) {
      setErrorText(
        'No connection with Vimeo.<br>Please, make sure your widget is connected to Vimeo in the widget editor.'
      );
    } else {
      setErrorText(null);
    }
  }, [userId, auth]);

  return (
    !!source?.length && (
      <DataWithCache
        sourceName={source}
        sourceType="videos"
        dataType="array"
        params={{ sort }}
      >
        {({ data, loading, hasNext, loadNext, error }) => {
          const hasData = !!(Array.isArray(data) && data.length);

          return (
            <LayoutContent
              data={data}
              loading={loading}
              hasNext={hasNext}
              loadNext={loadNext}
              error={error}
              errorText={errorText}
              setErrorText={setErrorText}
              hasData={hasData}
              textColor={textColor}
              currentVideo={currentVideo}
              setCurrentVideo={setCurrentVideo}
              setPlaying={setPlaying}
              setAutoplay={setAutoplay}
              setPercent={setPercent}
              sourceChanged={sourceChanged}
              setSourceChanged={setSourceChanged}
              LayoutArray={LayoutArray}
              mobile={mobile}
              widgetWidth={widgetWidth}
              nest={nest}
            />
          );
        }}
      </DataWithCache>
    )
  );
};

const LayoutContent = ({
  data,
  loading,
  hasNext,
  loadNext,
  error,
  errorText,
  setErrorText,
  hasData,
  textColor,
  currentVideo,
  setCurrentVideo,
  setPlaying,
  setAutoplay,
  setPercent,
  setSourceChanged,
  LayoutArray,
  mobile,
  widgetWidth,
  nest
}) => {
  useEffect(() => {
    if (error) {
      if (error.code === 429) {
        setErrorText(429);
      } else if (error.code === 400) {
        setErrorText(400);
      } else if (!errorText) {
        setErrorText(error.text);
      }
    }
  }, [error, errorText, setErrorText]);

  useEffect(() => {
    if (!hasData || !data || data.length === 0) {
      return;
    }

    const firstVideo = data[0];
    const isCurrentVideoInData =
      currentVideo && data.find((video) => currentVideo?.link === video?.link);

    if (!currentVideo || !isCurrentVideoInData) {
      setCurrentVideo(firstVideo);
      setSourceChanged(true);
    }
  }, [
    hasData,
    data?.length,
    data?.[0]?.link,
    currentVideo?.link,
    setCurrentVideo,
    setSourceChanged
  ]);

  if (error || errorText) {
    return <Error text={errorText} />;
  }

  if (!hasData) {
    return loading ? (
      <Loader
        size={18}
        color={textColor}
        className={`${classnamePrefix}-spinner-component`}
      />
    ) : (
      <Error text="Videos not found" />
    );
  }

  const setNextVideo = (videos) => {
    const currentIndex = videos.findIndex(
      (v) => v?.link === currentVideo?.link
    );

    if (currentIndex >= 0 && currentIndex + 1 < videos.length) {
      setCurrentVideo(videos[currentIndex + 1]);
      setPlaying(false);
      setAutoplay(true);
      setPercent(0);
    }
  };

  const nextVideo = () => {
    if (!hasData) return;

    const currentIndex = data.findIndex((v) => v?.link === currentVideo?.link);
    if (hasNext && currentIndex >= 0 && currentIndex + 1 >= data.length) {
      loadNext().then((resolve) => {
        setNextVideo(resolve);
      });
    } else {
      setNextVideo(data);
    }
  };

  return (
    <>
      <LayoutArray
        loadNext={loadNext}
        loading={loading}
        hasNext={hasNext}
        nextVideo={nextVideo}
        data={data}
        mobile={mobile}
        widgetWidth={widgetWidth}
      >
        {data?.map((item, id) => (
          <VideoCard
            data={item}
            key={id}
            number={id + 1}
            count={data.length}
            mobile={mobile}
          />
        ))}
      </LayoutArray>

      <Portal containerElement={nest}>
        <Popup nextVideo={nextVideo} />
      </Portal>
    </>
  );
};
